import React from 'react'
import styled from 'styled-components'
import { FormatMessageType, Options } from '../../../common/types'
import {
  FormSubGroup,
  FormSubGroupHeader,
  FormSubGroupHeading,
  FormSubGroupWrapper,
  Section,
  SectionColumn,
  Row,
  Column,
} from '../../../components/layout'
import {
  DateInput,
  SelectInput,
  TextAreaInput,
  TextInput,
  TimeInput,
  RadioButtonGroup,
} from '../../../components/layout/form/input'

import { translateInputOptions } from '../../../util/translateInputOptions'
import { serviceTypeOptions } from '../options'
import { useSettings } from '../../Parameters'

interface FormContentsProps {
  formatMessage: FormatMessageType
  showTowingDateSpan: boolean
  showEstimatedServiceType?: boolean
}

const DateSectionColumn = styled(SectionColumn as any)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
`

const HalfDateInput = styled(DateInput as any)`
  width: calc(50% - 1rem);
  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    width: calc(50% - 0.125rem);
  }
`

const HalfTimeInput = styled(TimeInput as any)`
  width: calc(50% - 1rem);
  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    width: calc(50% - 0.125rem);
  }
`

const HalfTextInput = styled(TextInput as any)`
  width: calc(50% - 1rem);
  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    width: calc(50% - 0.125rem);
  }
`

export const SelectSectionColumn = styled(SectionColumn as any)`
  gap: 2rem;
  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    gap: 0.25rem;
  }
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
`

const Divider = styled.div`
  display: flex;
  align-items: center;
  height: ${props => props.theme.inputFieldHeight};
  padding-top: 3px;

  &::after {
    content: '-';
  }
  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    display: none;
  }
`

export const orderScheduleOptions: Options = {
  options: {
    single: 'towingOrder.scheduling_fixed',
    span: 'towingOrder.scheduling_flexible',
  },
}

export const TowingOrderDetailsSubFormContents: React.FunctionComponent<FormContentsProps> = ({
  formatMessage,
  showTowingDateSpan
}) => {
  const translatedOrderScheduleOptions = translateInputOptions(orderScheduleOptions, formatMessage)
  const translatedServiceTypeOptions = translateInputOptions(serviceTypeOptions, formatMessage)
  const settings = useSettings()

  if (!settings) {
    return null
  }

  return (
    <FormSubGroupWrapper>
      <FormSubGroupHeader>
        <Row justify="space-between">
          <Column>
            <FormSubGroupHeading>{formatMessage({ id: 'towingCommon.jobDetails.jobInfoHeader' })}</FormSubGroupHeading>
          </Column>
          <Column>
            <RadioButtonGroup
              label={''}
              name="jobDetails.towingDateType"
              options={translatedOrderScheduleOptions}
            ></RadioButtonGroup>
          </Column>
        </Row>
      </FormSubGroupHeader>
      <FormSubGroup>
        {showTowingDateSpan ? (
          <Section columns={2}>
            <DateSectionColumn>
              <HalfDateInput
                label={formatMessage({ id: 'towingOrder.alTimeChangeModal.startDateLabel' })}
                name="jobDetails.towingDate"
                required
              />
              <Divider />
              <HalfDateInput
                label={formatMessage({ id: 'towingOrder.alTimeChangeModal.endDateLabel' })}
                name="jobDetails.towingEndDate"
              />
            </DateSectionColumn>

            <DateSectionColumn>
              <HalfTimeInput
                label={formatMessage({ id: 'towingOrder.alTimeChangeModal.startTimeLabel' })}
                name="jobDetails.towingTime"
              />
              <Divider />
              <HalfTimeInput
                label={formatMessage({ id: 'towingOrder.alTimeChangeModal.endTimeLabel' })}
                name="jobDetails.towingEndTime"
              />
            </DateSectionColumn>
          </Section>
        ) : (
          <Section columns={2} marginMobile="0">
            <DateSectionColumn>
              <HalfDateInput
                label={formatMessage({ id: 'towingCommon.jobDetails.towingDateLabel' })}
                name="jobDetails.towingDate"
                required
              />
              <HalfTimeInput
                label={formatMessage({ id: 'towingCommon.jobDetails.towingTimeLabel' })}
                name="jobDetails.towingTime"
              />
            </DateSectionColumn>
            <SectionColumn />
          </Section>
        )}

        <Section columns={2}>
          <SelectSectionColumn>
            <SelectInput
              label={formatMessage({ id: 'towingCommon.jobDetails.towingReasonLabel' })}
              name="jobDetails.towingReason"
              options={settings.towingReasons.map((label: string) => ({
                label,
                value: label,
              }))}
            />
            <SelectInput
              label={formatMessage({ id: 'towingCommon.jobDetails.preliminaryServiceTypeLabel' })}
              name="jobDetails.serviceType"
              options={translatedServiceTypeOptions}
            />
          </SelectSectionColumn>
          <DateSectionColumn>
            <HalfTextInput
              label={formatMessage({ id: 'towingCommon.jobDetails.orderReferenceLabel' })}
              name="jobDetails.orderReference"
            />
            <HalfTextInput
              label={formatMessage({ id: 'towingCommon.jobDetails.incidentCaseNoLabel' })}
              name="jobDetails.incidentCaseNo"
            />
          </DateSectionColumn>
        </Section>

        <Section columns={2}>
          <SectionColumn>
            <TextAreaInput
              label={formatMessage({ id: 'towingCommon.jobDetails.additionalInfoLabel' })}
              name="jobDetails.additionalInfo"
            />
          </SectionColumn>
          <SectionColumn>
            <TextAreaInput
              label={formatMessage({ id: 'towingCommon.jobDetails.notesLabel' })}
              name="jobDetails.notes"
            />
          </SectionColumn>
        </Section>
      </FormSubGroup>
    </FormSubGroupWrapper>
  )
}
